import React, { useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Textarea } from '../ui';
import { logger } from 'kmstack-shared/core';
import { useAlert } from 'kmstack-shared/hooks';

const salesSchema = z.object({
    name: z.string().min(2, "Full name is required."),
    companyName: z.string().min(2, "Company name is required."),
    email: z.string().email("Please enter a valid email address."),
    phone: z.string().optional(),
    message: z.string().min(10, "Please tell us a bit about your needs (min. 10 characters).").max(1000),
});

type SalesFormData = z.infer<typeof salesSchema>;

interface ContactSalesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ContactSalesModal: React.FC<ContactSalesModalProps> = ({ isOpen, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { notify } = useAlert();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isDirty }
    } = useForm<SalesFormData>({
        resolver: zodResolver(salesSchema),
    });

    const onSubmit = async (data: SalesFormData) => {
        setIsSubmitting(true);
        // In a real app, you would send this data to your CRM or sales email endpoint.
        // e.g., await api.post('/sales-inquiry', data);
        logger.dev("Sales Inquiry:", data);

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        setIsSubmitting(false);
        onClose();
        reset();
        notify({
            type: 'success',
            title: 'Message Sent!',
            message: 'Our sales team will get back to you shortly.',
            duration: 5000,
        });
    };

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
        reset();
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="bg-white sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="text-2xl">Contact Sales</DialogTitle>
                    <DialogDescription>
                        Tell us about your needs, and we'll get in touch to discuss our Enterprise solution.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label htmlFor="name">Full Name</Label>
                                <Input id="name" {...register("name")} placeholder="Jane Doe" />
                                {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="companyName">Company Name</Label>
                                <Input id="companyName" {...register("companyName")} placeholder="Acme Inc." />
                                {errors.companyName && <p className="text-sm text-red-500">{errors.companyName.message}</p>}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="email">Work Email</Label>
                            <Input id="email" {...register("email")} placeholder="jane.doe@acme.com" />
                            {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="phone">Phone Number <span className="text-gray-400">(Optional)</span></Label>
                            <Input id="phone" {...register("phone")} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="message">Message</Label>
                            <Textarea className='min-h-[100px]' id="message" {...register("message")} placeholder="Describe your requirements, team size, or any specific features you need." />
                            {errors.message && <p className="text-sm text-red-500">{errors.message.message}</p>}
                        </div>
                    </div>
                    <DialogFooter className="border-t pt-6">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                        <Button type="submit" disabled={!isDirty || isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : "Send Message"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
